import { BibleConstant } from "./BibleConstant.es2023.js"
import { TPPageState } from "./TPPageState.es2023.js";

/**
 * @typedef {import('./DText.js').DAddress} DAddress
 */

export class BibleConstantHelper {
    /** @type {Object.<string, number>} */
    static _mapName2Id;

    /**
     * 所有名稱，對應到 book id。
     * 所有，指的是繁體、簡體、英文，完整名稱、縮寫、短縮等，還有約一、約壹等別名。
     * @returns {Object.<string, number>}
     */
    static getMapName2Id() {
        if (!this._mapName2Id) {
            this._mapName2Id = this.#generateMapName2Id();
        }
        return this._mapName2Id;
    }

    /**
     * 取得 1-based 的 book id.
     * 
     * BookNameAndId，在 .ts 中相關的功能。
     * @param {string} name 若是英文，要小寫
     * @returns 若不存在，回傳 -1
     */
    static getBookId(name) {
        const map = this.getMapName2Id();
        return map[name] ?? -1;
    }

    /**
     * 產生範圍內的經文地址，即 創1:4-7
     * @param {number} book
     * @param {number} chap
     * @param {number} verse1
     * @param {number} verse2
     * @returns {DAddress[]}
     */
    static generateAddressesTpE(book, chap, verse1, verse2) {
        const range = verse2 > verse1
            ? Array.from({ length: verse2 - verse1 + 1 }, (_, i) => verse1 + i)
            : Array.from({ length: verse1 - verse2 + 1 }, (_, i) => verse2 + i);
        return range.map(v => ({ book, chap, verse: v }));
    }

    /**
     * 產生從指定節到章末的經文地址，即 創1:5-end
     * @param {number} book
     * @param {number} chap
     * @param {number} verse
     * @returns {DAddress[]}
     */
    static generateAddressesTpB(book, chap, verse) {
        const cnt = BibleConstant.COUNT_OF_VERSE[book - 1][chap - 1];
        return Array.from({ length: cnt - verse + 1 }, (_, i) => ({ book, chap, verse: verse + i }));
    }

    /**
     * 產生整章的經文地址，即 創2
     * @param {number} book
     * @param {number} chap
     * @returns {DAddress[]}
     */
    static generateAddressesTpF(book, chap) {
        const cnt = BibleConstant.COUNT_OF_VERSE[book - 1][chap - 1];
        return Array.from({ length: cnt }, (_, i) => ({ book, chap, verse: i + 1 }));
    }

    /**
     * 產生跨章的經文地址，即 創1:2-3:4
     * @param {number} book
     * @param {number} chap1
     * @param {number} verse1
     * @param {number} chap2
     * @param {number} verse2
     * @returns {DAddress[]}
     */
    static generateAddressesTpA(book, chap1, verse1, chap2, verse2) {
        const part1 = this.generateAddressesTpB(book, chap1, verse1);
        const part2 = [];
        if (chap2 - chap1 > 1) {
            for (let ch = chap1 + 1; ch < chap2; ch++) {
                part2.push(...this.generateAddressesTpF(book, ch));
            }
        }
        const part3 = this.generateAddressesTpE(book, chap2, 1, verse2);
        return part1.concat(part2, part3);
    }

    /**
     * 根據類型取得書卷名稱陣列
     * @param {'羅'|'羅馬書'|'罗'|'罗马书'|'romans'|'rom'|'ro'} tp
     * @returns {string[]}
     */
    static getBookNameArrayWhereTp(tp) {
        switch (tp) {
            case '羅': return BibleConstant.CHINESE_BOOK_ABBREVIATIONS;
            case '罗': return BibleConstant.CHINESE_BOOK_ABBREVIATIONS_GB;
            case '羅馬書': return BibleConstant.CHINESE_BOOK_NAMES;
            case '罗马书': return BibleConstant.CHINESE_BOOK_NAMES_GB;
            case 'romans': return BibleConstant.ENGLISH_BOOK_NAMES;
            case 'rom': return BibleConstant.ENGLISH_BOOK_ABBREVIATIONS;
            case 'ro': return BibleConstant.ENGLISH_BOOK_SHORT_ABBREVIATIONS;
            default: return BibleConstant.CHINESE_BOOK_ABBREVIATIONS;
        }
    }

    /**
     * 取得中文短書卷名稱
     * @param {boolean} isGb
     * @returns {string[]}
     */
    static getBookNameArrayChineseShort(isGb = null) {
        if (isGb == null) {
            isGb = TPPageState.s.gb == 1 ? true : false
        }

        return isGb ? BibleConstant.CHINESE_BOOK_ABBREVIATIONS_GB : BibleConstant.CHINESE_BOOK_ABBREVIATIONS;
    }

    /**
     * 取得中文完整書卷名稱
     * @param {boolean} isGb
     * @returns {string[]}
     */
    static getBookNameArrayChineseFull(isGb = null) {
        if (isGb == null) {
            isGb = TPPageState.s.gb == 1 ? true : false
        }
        return isGb ? BibleConstant.CHINESE_BOOK_NAMES_GB : BibleConstant.CHINESE_BOOK_NAMES;
    }

    /**
     * 取得英文標準書卷名稱 'Gen', 'Ex', 'Lev', 'Num'...
     * @returns {string[]}
     */
    static getBookNameArrayEnglishNormal() {
        return BibleConstant.ENGLISH_BOOK_ABBREVIATIONS;
    }
    /**
     * 取得英文短書卷名稱 'Ge', 'Ex', 'Le', 'Nu'... 像 TSK 就是用這種格式
     * @returns {string[]}
     */
    static getBookNameArrayEnglishShort(){
        return BibleConstant.ENGLISH_BOOK_SHORT_ABBREVIATIONS;
    }

    /**
     * 取得章節的節數
     * @param {number} book 1-based, book id
     * @param {number} chap 1-based, chap
     * @returns {number}
     */
    static getCountVerseOfChap(book, chap) {
        if (book < 1 || book > 66) {
            console.error(`Invalid book number: ${book}. It should be between 1 and 66.`);
            return 0
        }
        if (chap < 1 || chap > BibleConstant.COUNT_OF_VERSE[book - 1].length) {
            console.error(`Invalid chapter number: ${chap}. It should be between 1 and ${BibleConstant.COUNT_OF_VERSE[book - 1].length}.`);
            return 0
        }
        return BibleConstant.COUNT_OF_VERSE[book - 1][chap - 1];
    }
    static getCountChapOfBook(book) {
        if (book < 1 || book > 66) {
            console.error(`Invalid book number: ${book}. It should be between 1 and 66.`);
            return 0
        }
        return BibleConstant.COUNT_OF_CHAP[book - 1];
    }
    /**
     * @param {DAddress} address 
     * @param {boolean} isStopAtBookEnd 
     * @returns {DAddress|null}
     */
    static getNextAddress(address, isStopAtBookEnd = false) {
        if ( false == Array.isArray(address)){
            address = [address.book ?? 1, address.chap ?? 1, address?.verse ?? address?.sec ?? 1]
        }
        let [book, chap, verse] = address;

        const countVerse = this.getCountVerseOfChap(book, chap);
        if (verse < countVerse) {
            verse++;
        } else {
            verse = 1;
            const countChap = this.getCountChapOfBook(book);
            if (chap < countChap) {
                chap++;
            } else {
                if (isStopAtBookEnd) {
                    return null; // 已經到書卷末尾，返回 null
                } else {
                    book++;
                    chap = 1;
                }
            }
        }
        return [book, chap, verse];
    }
    /**
     * 內部方法：產生名稱到 ID 的映射
     * @returns {Object.<string, number>}
     */
    static #generateMapName2Id() {
        const sources = [
            BibleConstant.CHINESE_BOOK_NAMES,
            BibleConstant.CHINESE_BOOK_NAMES_GB,
            BibleConstant.CHINESE_BOOK_ABBREVIATIONS,
            BibleConstant.CHINESE_BOOK_ABBREVIATIONS_GB,
            BibleConstant.ENGLISH_BOOK_ABBREVIATIONS,
            BibleConstant.ENGLISH_BOOK_NAMES,
            BibleConstant.ENGLISH_BOOK_SHORT_ABBREVIATIONS
        ];

        /** @type {Object.<string, number>} */
        const map = {};
        sources.forEach(source => {
            source.forEach((name, index) => {
                map[name.toLowerCase()] = index + 1;
            });
        });

        // 特殊名稱處理 (註腳中，馬太5:41 cnet)
        const specialNames = [
            { id: 62, names: ['約壹', '约壹', '約翰壹書', '约翰壹书', '約翰一書', '约翰一书', '約一', '约一'] },
            { id: 63, names: ['約貳', '约贰', '約翰貳書', '约翰贰书', '約翰二書', '约翰二书', '約二', '约二'] },
            { id: 64, names: ['約參', '约参', '約翰參書', '约翰参书', '約翰三書', '约翰三书', '约三', '約三'] },
            { id: 40, names: ['馬太', '马太'] },
            { id: 41, names: ['馬可', '马可'] },
            { id: 42, names: ['路加', '路加'] },
            { id: 43, names: ['約翰', '约翰'] },
        ];
        specialNames.forEach(({ id, names }) => {
            names.forEach(name => {
                map[name] = id;
            });
        });

        return map;
    }
    /**
     * 取得只有一章的書卷 ID 陣列
     * @returns {number[]}
     */
    static getChapCountEqual1BookIds() {
        const ref = BibleConstant.COUNT_OF_CHAP;
        return ref.map((cnt, i) => (cnt === 1 ? i + 1 : null)).filter(id => id !== null);
    }
}